/**
 * relationalMemoryEngine.js — FASE R5 emotional anchor store.
 *
 * DISTINCT FROM kevia-relationships/relationalMemoryEngine.js (pastoral signals, 7-day TTL).
 * Key prefix: kevia_ems_v1_ — no collision with existing stores.
 *
 * Stores emotional anchors — compressed warm observations about a person's
 * emotional state over weeks and months. NOT pastoral signal tones. NOT visit data.
 *
 * Emotionally-weighted TTL:
 *   crisis (grief, suicide, burnout crisis)  → 90 days
 *   burnout (pastoral exhaustion)            → 60 days
 *   pastoral_process (discernment journey)   → 45 days
 *   recurring (conflict, tension)            → 30 days
 *   standard                                 → 14 days
 *   logistics / planning                     → NOT stored
 *
 * What gets stored (warm language, not clinical):
 *   emotionalAnchor:   "cansancio_acumulado" | "dolor_activo" | "busqueda_espiritual" | etc.
 *   unresolvedTension: "conflicto_liderazgo" | "tension_familiar" | etc.
 *   lastTone:          pastoral mood ("heavy" | "hopeful" | "fragile" | "stable" | "uncertain")
 *   careIntensity:     "light" | "moderate" | "high" | "crisis"
 *   milestoneNote:     "restauracion" | "decision_espiritual" | null
 *   recurrenceCount:   how many times same anchor appeared
 *   decayCategory:     one of the 5 tiers above
 */

const KEY_PREFIX  = "kevia_ems_v1_";
const MAX_ENTITIES = 60;

// Emotionally-weighted TTL (ms)
const DECAY_TTL = {
  crisis:            90 * 24 * 60 * 60 * 1000,
  burnout:           60 * 24 * 60 * 60 * 1000,
  pastoral_process:  45 * 24 * 60 * 60 * 1000,
  recurring:         30 * 24 * 60 * 60 * 1000,
  standard:          14 * 24 * 60 * 60 * 1000,
};

// processState → decayCategory
const PROCESS_TO_DECAY = {
  "quebranto":              "crisis",
  "agotamiento_espiritual": "burnout",
  "resistencia":            "recurring",
  "búsqueda":               "pastoral_process",
  "restauración":           "pastoral_process",
  "crecimiento":            "standard",
  "confusión":              "recurring",
  "apertura":               "standard",
};

// Mood → lastTone
const MOOD_TO_TONE = {
  "grief":         "heavy",
  "urgent":        "heavy",
  "reflective":    "uncertain",
  "pastoral-care": "fragile",
  "celebration":   "hopeful",
  "neutral":       "stable",
  "deep-focus":    "uncertain",
};

/**
 * @typedef {{
 *   entityId:          string,
 *   entityType:        string,
 *   ts:                number,
 *   emotionalAnchor:   string|null,
 *   anchorIntensity:   "light"|"moderate"|"heavy",
 *   unresolvedTension: string|null,
 *   lastTone:          "heavy"|"hopeful"|"fragile"|"stable"|"uncertain",
 *   careIntensity:     "light"|"moderate"|"high"|"crisis",
 *   milestoneNote:     string|null,
 *   recurrenceCount:   number,
 *   decayCategory:     string,
 * }} EmotionalMemory
 */

function _key(entityId) {
  return KEY_PREFIX + entityId;
}

function _allKeys() {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(KEY_PREFIX)) keys.push(k);
    }
  } catch { /* private browsing */ }
  return keys;
}

function _pruneOverflow() {
  const keys = _allKeys();
  if (keys.length <= MAX_ENTITIES) return;
  // Remove the oldest entry when over limit
  let oldestKey = null, oldestTs = Infinity;
  for (const k of keys) {
    try {
      const r = JSON.parse(localStorage.getItem(k) ?? "{}");
      if ((r.ts ?? 0) < oldestTs) { oldestTs = r.ts; oldestKey = k; }
    } catch { /* ignore */ }
  }
  if (oldestKey) {
    try { localStorage.removeItem(oldestKey); } catch { /* ignore */ }
  }
}

/**
 * Load emotional memory for an entity, or null if none / expired.
 * @param {string} entityId
 * @returns {EmotionalMemory|null}
 */
export function loadEmotionalMemory(entityId) {
  if (!entityId) return null;
  try {
    const raw = localStorage.getItem(_key(entityId));
    if (!raw) return null;
    const record = JSON.parse(raw);
    const ttl    = DECAY_TTL[record.decayCategory] ?? DECAY_TTL.standard;
    if (Date.now() - (record.ts ?? 0) > ttl) {
      // Expired — clean up
      try { localStorage.removeItem(_key(entityId)); } catch { /* ignore */ }
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

/**
 * Save or update emotional memory for an entity.
 * Merges partial updates — only provided fields are overwritten.
 *
 * @param {string} entityId
 * @param {string} entityType
 * @param {Partial<EmotionalMemory>} update
 */
export function saveEmotionalMemory(entityId, entityType, update) {
  if (!entityId) return;
  try {
    _pruneOverflow();
    const existing = loadEmotionalMemory(entityId) ?? {
      entityId,
      entityType,
      ts:                Date.now(),
      emotionalAnchor:   null,
      anchorIntensity:   "light",
      unresolvedTension: null,
      lastTone:          "stable",
      careIntensity:     "light",
      milestoneNote:     null,
      recurrenceCount:   0,
      decayCategory:     "standard",
    };

    // Track recurrence: if anchor is same as last, increment count
    const sameAnchor = update.emotionalAnchor && update.emotionalAnchor === existing.emotionalAnchor;

    const updated = {
      ...existing,
      ...update,
      entityId,
      entityType,
      ts:             Date.now(),
      recurrenceCount: sameAnchor
        ? Math.min(existing.recurrenceCount + 1, 10)
        : (update.emotionalAnchor ? 1 : existing.recurrenceCount),
    };

    localStorage.setItem(_key(entityId), JSON.stringify(updated));
  } catch { /* quota or private browsing */ }
}

/**
 * Update emotional memory from an orchestration result.
 * Called after each LLM conversation that involved an entity.
 *
 * @param {{
 *   entityId:     string,
 *   entityType:   string,
 *   processState: string,   — from judgment.processState
 *   mood:         string,   — from resolveKeviaMood result
 *   pastoralState: object,  — from FASE R3 pastoralStateCoordinator result
 *   milestoneType: string|null,
 * }} params
 */
export function updateFromConversation({ entityId, entityType, processState, mood, pastoralState, milestoneType }) {
  if (!entityId) return;

  const anchor        = PROCESS_TO_ANCHOR[processState] ?? null;
  const decayCategory = PROCESS_TO_DECAY[processState] ?? "standard";
  const lastTone      = MOOD_TO_TONE[mood] ?? "stable";

  // Determine care intensity from pastoralState
  let careIntensity = "light";
  if (pastoralState?.escalation === "refer_professional")               careIntensity = "crisis";
  else if (pastoralState?.escalation === "involve_leadership")          careIntensity = "high";
  else if (pastoralState?.capacity === "overwhelmed" || pastoralState?.exhaustionState === "saturación_crítica") careIntensity = "high";
  else if (pastoralState?.capacity === "depleted" || pastoralState?.isolationState !== "acompañado") careIntensity = "moderate";

  // Derive unresolved tension
  let unresolvedTension = null;
  if (pastoralState?.isolationState === "aislamiento_profundo")         unresolvedTension = "aislamiento_pastoral";
  else if (pastoralState?.responsibilityState === "centralización_crítica") unresolvedTension = "sobrecarga_central";

  // Don't save logistics/standard conversations if there's no meaningful signal
  if (!anchor && careIntensity === "light" && lastTone === "stable" && !milestoneType) return;

  saveEmotionalMemory(entityId, entityType, {
    emotionalAnchor:   anchor,
    anchorIntensity:   careIntensity === "crisis" ? "heavy" : careIntensity === "high" ? "moderate" : "light",
    unresolvedTension,
    lastTone,
    careIntensity,
    milestoneNote:     milestoneType ?? null,
    decayCategory:     careIntensity === "crisis" ? "crisis" : decayCategory,
  });
}

/**
 * @param {string} entityId
 */
export function clearEmotionalMemory(entityId) {
  if (!entityId) return;
  try { localStorage.removeItem(_key(entityId)); } catch { /* ignore */ }
}

// ── Internal mapping (used by emotionalContinuityTracker) ──────────────────

export const PROCESS_TO_ANCHOR = {
  "quebranto":              "dolor_activo",
  "agotamiento_espiritual": "cansancio_acumulado",
  "resistencia":            "resistencia_pastoral",
  "búsqueda":               "busqueda_espiritual",
  "restauración":           "restauracion_emergente",
  "crecimiento":            "crecimiento_estable",
  "confusión":              "confusion_espiritual",
  "apertura":               null,
};
